import { toast } from "sonner";
import { logger } from "@/lib/logger";
import ENV from "@/lib/env";



// ── Config ─────────────────────────────────────────────────────────────────────
const API_URL = ENV.FACE_BIOMETRIC_API_BASE_URL || 'https://vtx3qf6sfd.us-east-1.awsapprunner.com';

// ── Auth ───────────────────────────────────────────────────────────────────────
// Reuses the jwt_token stored by biometricAuthService.login() at sign-in time.
// No need to generate a new JWT here.
// function getAuthToken(): string {
//   const token = localStorage.getItem('jwt_token');
//   if (!token) throw new Error('No auth token found. Please log in again.');
//   return token;
// }

// ── Error Helpers ──────────────────────────────────────────────────────────────
function isCORSError(error: any): boolean {
  return (
    error?.message?.includes('CORS') ||
    error?.message?.includes('Access-Control-Allow-Origin') ||
    error?.message?.includes('net::ERR_FAILED') ||
    (error?.name === 'TypeError' && error?.message?.includes('Failed to fetch'))
  );
}

function isNetworkError(error: any): boolean {
  return (
    error?.message?.includes('Failed to fetch') ||
    error?.message?.includes('Network request failed') ||
    error?.message?.includes('net::ERR_') ||
    error?.name === 'TypeError'
  );
}

// ── Fetch with Retry ───────────────────────────────────────────────────────────
async function fetchWithRetry(url: string, options: RequestInit, maxRetries: number = 2): Promise<Response> {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      logger.info(`[SMS] Attempt ${attempt + 1}/${maxRetries + 1} to send SMS`);
      return await fetch(url, options);
    } catch (error) {
      lastError = error;
      logger.warn(`[SMS] Attempt ${attempt + 1} failed:`, error);

      if (isCORSError(error)) {
        logger.error('[SMS] CORS error detected, not retrying:', error);
        throw error;
      }

      if (isNetworkError(error) && !isCORSError(error) && attempt < maxRetries) {
        logger.info(`[SMS] Retrying in ${(attempt + 1) * 1000}ms due to network issue...`);
        await new Promise(resolve => setTimeout(resolve, (attempt + 1) * 1000));
        continue;
      }

      throw error;
    }
  }

  throw lastError;
}

// ── Send Single SMS ────────────────────────────────────────────────────────────
import { apiPost } from '@/lib/api';

export async function sendSMS(to: string, body: string, recipientName?: string): Promise<boolean> {
  try {
    const displayName = recipientName || to;
    const loadingToast = toast.loading(`Sending SMS to ${displayName}...`);
    await apiPost('/send-sms', { to, body });
    toast.dismiss(loadingToast);
    toast.success(`SMS sent to ${displayName}`);
    return true;
  } catch (error) {
    const displayName = recipientName || to;
    toast.error(`Failed to send SMS to ${displayName}`, {
      description: error instanceof Error ? error.message : 'Unknown error'
    });
    return false;
  }
}

// ── Send Bulk SMS ──────────────────────────────────────────────────────────────
export async function sendBulkSMS(messages: Array<{ to: string; body: string; name?: string }>): Promise<{ success: boolean; count: number }> {
  if (messages.length === 0) return { success: false, count: 0 };

  logger.info('[SMS] Sending bulk messages', {
    count: messages.length,
    recipients: messages.map(m => m.to), // Note: logger only logs in dev
    timestamp: new Date().toISOString()
  });

  try {
    const progressToast = toast.loading(`Sending ${messages.length} SMS messages...`);
    const results: boolean[] = [];

    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const displayName = (msg as any).name || msg.to;
      toast.loading(`Sending SMS ${i + 1}/${messages.length} to ${displayName}...`, { id: progressToast });
      results.push(await sendSMS(msg.to, msg.body, (msg as any).name));
      if (i < messages.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    const successCount = results.filter(Boolean).length;
    const failedCount = messages.length - successCount;
    const success = successCount === messages.length;

    if (success) {
      toast.success(`Successfully sent ${successCount} message${successCount !== 1 ? 's' : ''}`, {
        id: progressToast, duration: 3000
      });
    } else if (successCount > 0) {
      toast.warning(`Sent ${successCount} of ${messages.length} messages (${failedCount} failed)`, {
        id: progressToast, duration: 5000
      });
    } else {
      toast.error('Failed to send all messages', { id: progressToast, duration: 5000 });
    }

    return { success, count: successCount };

  } catch (error) {
    logger.error('[SMS] Error in sendBulkSMS:', error);
    toast.error('An error occurred while sending messages');
    return { success: false, count: 0 };
  }
}

// ── Health Check ───────────────────────────────────────────────────────────────
export async function checkSMSHealth(): Promise<boolean> {
  try {
    const response = await fetchWithRetry(`${API_URL}/health`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    }, 1);

    const data = await response.json();
    if (data.status === 'ok') {
      logger.info('[SMS] Health check passed', data);
      return true;
    }
    return false;
  } catch (error) {
    logger.error('[SMS] Health check failed', error);
    if (isCORSError(error)) return true;
    return false;
  }
}

// ── Service Info ───────────────────────────────────────────────────────────────
export function getSMSServiceInfo() {
  return {
    provider: 'AWS SNS',
    apiUrl: API_URL,
    authenticated: true,
    features: [
      'JWT from localStorage (jwt_token)',
      'Auto Retry Logic',
      'Bulk SMS',
      'Health Checks',
      'CORS Error Handling',
    ]
  };
}